import {LanguageDefinition, Token} from '../types';

/**
 * MOTOMAN INFORM言語のキーワード定義
 */
const INFORM_KEYWORDS = {
  motion: ['MOVJ', 'MOVL', 'MOVC', 'MOVS', 'IMOV', 'SPEED', 'REFP'],
  control: [
    'JUMP',
    'CALL',
    'TIMER',
    'RET',
    'NOP',
    'PAUSE',
    'CWAIT',
    'NWAIT',
    'ENWAIT',
    'MSG',
    'ADVINIT',
    'ADVSTOP',
    'END',
    'PSTART',
    'PWAIT',
    'NSRCH'
  ],
  welding: ['ARCON', 'ARCOF', 'VWELD', 'AWELD', 'ARCSET', 'WVON', 'WVOF', 'ARCCTS', 'ARCCTE'],
  condition: [
    'IF',
    'ELSE',
    'SWITCH',
    'ENDSWITCH',
    'IFTHEN',
    'ENDIF',
    'ANDIF',
    'ORIF',
    'ELSEIF',
    'UNTIL',
    'WHILE',
    'ENDWHILE',
    'FOREVER'
  ],
  io: ['DOUT', 'DIN', 'WAIT', 'PULSE', 'AOUT', 'ARATION', 'ARATIOF', 'ANTOUT', 'GETS', 'SETS'],
  operation: [
    'CLEAR',
    'INC',
    'DEC',
    'SET',
    'ADD',
    'SUB',
    'MUL',
    'DIV',
    'CNVRT',
    'AND',
    'OR',
    'NOT',
    'XOR',
    'MFRAME',
    'SETE',
    'GETE',
    'SQRT',
    'SIN',
    'COS',
    'ATAN',
    'MULMAT',
    'INVMAT',
    'SETFILE',
    'GETFILE',
    'SETREG',
    'GETREG'
  ],
  keyword: ['ON', 'OFF', 'VJ', 'VR', 'PL']
};

/**
 * INFORM言語の行をトークンに分割する
 */
const tokenizeLine = (line: string): Token[] => {
  const tokens: Token[] = [];
  const trimmedLine = line.trim();

  // 空行
  if (trimmedLine.length === 0) {
    tokens.push({type: 'text', value: line});
    return tokens;
  }

  // ヘッダー情報（///で始まる行）
  if (trimmedLine.startsWith('///')) {
    tokens.push({type: 'header', value: line});
    return tokens;
  }

  // ディレクティブ（//で始まる行）
  if (trimmedLine.startsWith('//')) {
    tokens.push({type: 'directive', value: line});
    return tokens;
  }

  // セクションヘッダー（/JOBなど）
  if (trimmedLine.startsWith('/')) {
    tokens.push({type: 'directive', value: line});
    return tokens;
  }

  // 位置データ行（C00000=...など）
  if (/^[A-Z]\d{5}=/.test(trimmedLine)) {
    const match = trimmedLine.match(/^([A-Z]\d{5})(=)(.*)$/);
    if (match) {
      const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
      if (leadingSpaces) {
        tokens.push({type: 'text', value: leadingSpaces});
      }
      tokens.push({type: 'variable', value: match[1]});
      tokens.push({type: 'text', value: match[2]});
      const values = match[3].split(',');
      values.forEach((val, idx) => {
        if (idx > 0) tokens.push({type: 'text', value: ','});
        tokens.push({type: 'number', value: val});
      });
      return tokens;
    }
  }

  // 通常のコード行をトークン化
  const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
  if (leadingSpaces) {
    tokens.push({type: 'text', value: leadingSpaces});
  }

  const codePart = line.substring(leadingSpaces.length);
  const tokenPattern =
    /(\$[A-Z][A-Z0-9]*|[A-Z]{2,}#\([^)]+\)|[A-Z]\d{5}|[A-Z]{2}\d{4}|[A-Z][A-Z0-9]*|[+-]?\d+\.?\d*|[=(),#]+|\s+)/gi;

  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(codePart)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({type: 'text', value: codePart.substring(lastIndex, match.index)});
    }

    const word = match[0];
    const upperWord = word.toUpperCase();

    if (/^\s+$/.test(word)) {
      tokens.push({type: 'text', value: word});
    } else if (/^[=(),#]+$/.test(word)) {
      tokens.push({type: 'text', value: word});
    } else if (/^[A-Z]{2}#\([^)]+\)$/i.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (/^\$[A-Z][A-Z0-9]*$/i.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (/^[A-Z]\d{5}$/i.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (/^[A-Z]{2}\d{4}$/i.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (INFORM_KEYWORDS.motion.includes(upperWord)) {
      tokens.push({type: 'motion', value: word});
    } else if (INFORM_KEYWORDS.control.includes(upperWord)) {
      tokens.push({type: 'control', value: word});
    } else if (INFORM_KEYWORDS.welding.includes(upperWord)) {
      tokens.push({type: 'welding', value: word});
    } else if (INFORM_KEYWORDS.condition.includes(upperWord)) {
      tokens.push({type: 'condition', value: word});
    } else if (INFORM_KEYWORDS.io.includes(upperWord)) {
      tokens.push({type: 'io', value: word});
    } else if (INFORM_KEYWORDS.operation.includes(upperWord)) {
      tokens.push({type: 'operation', value: word});
    } else if (INFORM_KEYWORDS.keyword.includes(upperWord)) {
      tokens.push({type: 'keyword', value: word});
    } else if (/^[+-]?\d+\.?\d*$/.test(word)) {
      tokens.push({type: 'number', value: word});
    } else {
      tokens.push({type: 'text', value: word});
    }

    lastIndex = match.index + word.length;
  }

  if (lastIndex < codePart.length) {
    tokens.push({type: 'text', value: codePart.substring(lastIndex)});
  }

  return tokens;
};

/**
 * MOTOMAN INFORM言語定義
 */
export const informLanguage: LanguageDefinition = {
  name: 'MOTOMAN INFORM',
  extensions: ['.jbi', '.jbl'],
  badge: 'INFORM',
  tokenizeLine
};
